<template>
  <div class="fill-height">
    <v-card class="pa-5 rounded-lg fill-height">
      <v-row>
        <v-col>
          <div class="text-h6">
            {{ $t("card_title.efforts") }}
          </div>
        </v-col>
        <v-col
          v-if="hasExerciseTimings"
          cols="auto"
        >
          <v-btn
            small
            :outlined="!showExerciseAnnotations"
            color="primary"
            class="text-none"
            @click="showPeriodAnnotations = false; showExerciseAnnotations = !showExerciseAnnotations"
          >
            {{ $t("button.show_exercises") }}
          </v-btn>
        </v-col>
        <v-col
          v-if="hasPeriods"
          cols="auto"
        >
          <v-btn
            small
            :outlined="!showPeriodAnnotations"
            color="primary"
            class="text-none"
            @click="showPeriodAnnotations = !showPeriodAnnotations; showExerciseAnnotations = false"
          >
            {{ $t("button.show_periods") }}
          </v-btn>
        </v-col>
        <v-col
          v-if="!removable"
          cols="auto"
        >
          <v-btn-toggle
            v-model="tab"
            color="primary"
            mandatory
          >
            <v-btn
              value="sprints"
              small
              class="text-none"
            >
              {{ $t("button.velocity") }}
            </v-btn>
            <v-btn
              value="acc"
              small
              class="text-none"
            >
              {{ $t("button.acceleration") }}
            </v-btn>
            <v-btn
              value="hr"
              small
              class="text-none"
            >
              {{ $t("button.heart_rate") }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col
          v-if="!removable"
          cols="auto"
        >
          <visual-list-toggle
            :value.sync="view"
          />
        </v-col>
      </v-row>
      <div
        v-if="rawData.loading"
        class="fill-height d-flex align-center justify-center"
      >
        <v-progress-circular
          color="grey lighten-1"
          :size="150"
          indeterminate
          :width="1"
        />
      </div>
      <div
        v-else-if="!hasEfforts"
        class="text-center fill-height d-flex align-center justify-center"
      >
        {{ $t("data_empty") }}
      </div>
      <div v-else>
        <v-row
          v-if="!removable"
          align="center"
        >
          <v-col
            cols="auto"
            class="grey--text text--darken-2 text-caption"
          >
            <strong>{{ tab === 'sprints' ? filteredSprints.length : tab === 'acc' ? filteredAcc.length : filteredHREfforts.length }}</strong> {{ $t("label.efforts") }}
          </v-col>
          <v-spacer />
          <v-col
            cols="auto"
            class="text-body-1 grey--text pr-2"
          >
            {{ $t('zones.title') }}
          </v-col>
          <v-col cols="auto">
            <v-btn-toggle
              v-model="selectedBands"
              dense
              multiple
            >
              <v-tooltip
                v-for="val in possibleBands"
                :key="val"
                top
              >
                <template v-slot:activator="{ on }">
                  <div
                    style="position: relative;"
                    v-on="on"
                  >
                    <v-btn
                      class="ml-1"
                      :value="val"
                      small
                      min-width="40"
                      :color="getColorForTooltip(val)"
                    >
                      {{ val }}
                      <v-sheet
                        v-if="selectedBands.includes(val)"
                        class="indicator"
                        elevation="12"
                        height="5"
                        width="5"
                        style="background-color: #000000; position: absolute; top: 25px; border-radius: 50%;"
                      />
                    </v-btn>
                  </div>
                </template>
                <span>
                  {{ getTooltipText(val) }}
                </span>
              </v-tooltip>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col v-if="view === 'chart'">
            <line-chart
              v-if="tab === 'sprints'"
              class="elevation-0"
              type="bar"
              :height="300"
              :raw-data="filteredSprints"
              :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('chart_legends.velocity') }]"
              x-value="start_time"
              :xaxis="{ title: {text: $t('picker.time.title') + '(min)'}, type: 'numeric', min: 0, max: $dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1 }"
              :yaxis="[{title: { text: $t('hr_variables.max_velocity_km_h') }, min: 0, max: 35, tickAmount: 5}]"
              :colors="[ '#FF8888' ]"
              :animations="{enabled: false}"
              :annotations="xAxisAnnotations"
            />
            <line-chart
              v-if="tab === 'acc'"
              class="elevation-0"
              type="bar"
              :height="300"
              :raw-data="filteredAcc"
              :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('chart_legends.acceleration') }]"
              x-value="start_time"
              :xaxis="{ title: {text: $t('picker.time.title') + '(min)'}, type: 'numeric', min: 0, max: $dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1}"
              :yaxis="[{title: { text: $t('mechanism.acceleration') + '(m/s²)' }, min: -4.5, max: 4.5, tickAmount: 5}]"
              :colors="[ '#00CC00' ]"
              :animations="{enabled: false}"
              :annotations="xAxisAnnotations"
            />
            <line-chart
              v-if="tab === 'hr'"
              class="elevation-0"
              type="bar"
              :height="300"
              :raw-data="filteredHREfforts"
              :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('performance.raw.variables.heart_rate') }]"
              x-value="start_time"
              :xaxis="{ title: {text: 'Time (min)'}, type: 'numeric', min: 0, max: $dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1}"
              :yaxis="[{title: { text: $t('hr_variables.max_heart_rate') }, min: 0, max: 210}]"
              :colors="[ '#E91E63' ]"
              :animations="{enabled: false}"
              :annotations="xAxisAnnotations"
            />
          </v-col>
          <v-col v-else>
            <v-data-table
              v-if="tab === 'sprints'"
              height="300"
              disable-pagination
              dense
              :headers="sprintHeaders"
              :items="filteredSprints"
              hide-default-footer
              fixed-header
            />
            <v-data-table
              v-else-if="tab === 'acc'"
              height="300"
              disable-pagination
              dense
              :headers="accHeaders"
              :items="filteredAcc"
              hide-default-footer
              fixed-header
            />
            <v-data-table
              v-else-if="tab === 'hr'"
              height="300"
              disable-pagination
              dense
              :headers="hrHeaders"
              :items="filteredHREfforts"
              hide-default-footer
              fixed-header
            />
          </v-col>
        </v-row>
      </div>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import i18n from '@/plugins/i18n'
import { EventApiService } from '@/data/services'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import dayjs from 'dayjs'

export default {
  name: 'EffortsChart',
  components: { VisualListToggle, LineChart },
  props: {
    event: {
      type: Object,
      required: true
    },
    player: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    parentEventId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      selectedBands: [3, 4, 5, 6, 7],
      tab: 'sprints',
      view: 'chart',
      showPeriodAnnotations: false,
      showExerciseAnnotations: false,
      sprintBandsColors: ['#FFBBBB', '#FFAAAA', '#FF9999', '#FF8888', '#FF7777', '#FF6666', '#FF5555'],
      accelerationBands: ['a < -4m/s²', '-4m/s² < a < -3m/s²', '-3m/s² < a < -2m/s²', '-2m/s² < a < 0', '0 < a < 2m/s²', '2m/s² < a < 3m/s²', '3m/s² < a < 4m/s²', 'a > 4m/s²'],
      sprintBands: ['0km/h < v < 7.2km/h', '7.2km/h < v < 14.4km/h', '14.4km/h < v < 21.6km/h', '21.6km/h < v < 25.2km/h', '25.2km/h < v < 28.8km/h', '28.8km/h < v < 32.4km/h', 'v > 32.4km/h'],
      hrBands: ['20 - 45', '45 - 55', '55 - 65', '65 - 75', '75 - 85', '85 - 95', '95 - 105', '105 - 200'],
      rawData: {
        data: {
          sprints: [],
          high_acc: [],
          hr: []
        },
        loading: false
      }
    }
  },
  computed: {
    // Efforts should always use parent event ID when viewing a period
    eventIdForEfforts() {
      return this.parentEventId || this.event.data.id
    },
    // Add computed properties for exercise and period detection
    hasExerciseTimings() {
      return this.event.data.training_exercises && this.event.data.training_exercises.length > 0 &&
        this.event.data.training_exercises.some(exercise => exercise.exercise_start_at && exercise.exercise_stop_at)
    },
    hasPeriods() {
      return this.event.data.periods && this.event.data.periods.length > 0
    },
    // Add computed property for x-axis annotations
    xAxisAnnotations() {
      const annotations = {
        xaxis: []
      }

      if (this.showPeriodAnnotations) {
        annotations.xaxis = this.periodAnnotations
      } else if (this.showExerciseAnnotations) {
        annotations.xaxis = this.exerciseAnnotations
      }

      return annotations
    },
    exerciseAnnotations() {
      const returnList = []
      const eventDate = dayjs(this.event.data.start_at)

      const processExercise = (exercise) => {
        if (!exercise.exercise_start_at || !exercise.exercise_stop_at) return

        const startTime = dayjs(exercise.exercise_start_at)
        const stopTime = dayjs(exercise.exercise_stop_at)

        returnList.push({
          x: eventDate.hour(startTime.hour()).minute(startTime.minute()).diff(eventDate, 'minute'),
          x2: eventDate.hour(stopTime.hour()).minute(stopTime.minute()).diff(eventDate, 'minute'),
          fillColor: '#e3f2fd',
          borderColor: '#BC291C',
          opacity: 0.3,
          label: {
            text: exercise.name || `${exercise.type.charAt(0).toUpperCase() + exercise.type.slice(1)}`,
            style: {
              background: '#BC291C',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        })
      }

      this.event.data.training_exercises.forEach(exercise => {
        switch (exercise.type) {
          case 'exercise':
            processExercise(exercise)
            break

          case 'group':
            if (exercise.exercises && exercise.exercises.length > 0) {
              exercise.exercises.forEach(groupExercise => processExercise(groupExercise))
            }
            break

          case 'superset':
            processExercise(exercise)
            break

          case 'pause':
            processExercise({
              ...exercise,
              name: this.$t('training_builder.pause')
            })
            break

          default:
            processExercise(exercise)
            break
        }
      })

      return returnList
    },
    periodAnnotations() {
      const returnList = []
      const eventStartMinutes = dayjs(this.event.data.start_at)

      this.event.data.periods.forEach(period => {
        const startMinutes = dayjs(period.start_at).diff(eventStartMinutes, 'minute', true)
        const stopMinutes = dayjs(period.end_at).diff(eventStartMinutes, 'minute', true)

        returnList.push({
          x: startMinutes,
          x2: stopMinutes,
          fillColor: '#e3f2fd',
          borderColor: '#BC291C',
          opacity: 0.3,
          label: {
            text: period.title,
            style: {
              background: '#BC291C',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        })
      })

      return returnList
    },
    filteredSprints() {
      if (!this.rawData.data?.sprints) return []

      if (this.selectedBands.length === 0) return this.rawData.data.sprints

      return this.rawData.data.sprints.filter(s => this.selectedBands.includes(s.band))
    },
    filteredAcc() {
      if (!this.rawData.data?.high_acc) return []

      if (this.selectedBands.length === 0) return this.rawData.data.high_acc

      return this.rawData.data.high_acc.filter(s => this.selectedBands.includes(s.band))
    },
    filteredHREfforts() {
      if (!this.rawData.data?.hr) return []

      if (this.selectedBands.length === 0) return this.rawData.data.hr

      return this.rawData.data.hr.filter(s => this.selectedBands.includes(s.band))
    },
    possibleBands() {
      if (this.tab === 'sprints') {
        return 7
      } else if (this.tab === 'acc') {
        return 8
      } else if (this.tab === 'hr') {
        return 8
      } else {
        return null
      }
    },
    hasEfforts() {
      if (this.tab === 'sprints') {
        return this.rawData.data?.sprints?.length > 0
      } else if (this.tab === 'acc') {
        return this.rawData.data?.high_acc?.length > 0
      } else if (this.tab === 'hr') {
        return this.rawData.data?.hr?.length > 0
      } else {
        return false
      }
    }
  },
  watch: {
    tab: function (value) {
      this.selectedBands = this.tab === 'sprints' ? [3, 4, 5, 6, 7] : []
      this.fetchPlayerRawData(value === 'sprints' ? 'vel_eff' : (value === 'acc' ? 'acc_eff' : (value === 'hr' ? 'hr_eff' : null)))
    }
  },
  created() {
    this.sprintHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: 'Vmax' },
      { value: 'average', text: 'Vavg' },
      { value: 'one_seconds_before', text: 'T-1s' },
      { value: 'two_seconds_before', text: 'T-2s' },
      { value: 'three_seconds_before', text: 'T-3s' }
    ]

    this.accHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: i18n.t('table_headers.performance.player_details.efforts.peek') },
      { value: 'average', text: i18n.t('table_headers.performance.player_details.efforts.avg_acc') },
      { value: 'one_seconds_before', text: 'T-1s' },
      { value: 'two_seconds_before', text: 'T-2s' },
      { value: 'three_seconds_before', text: 'T-3s' }
    ]

    this.hrHeaders = [
      { value: 'start_time', text: 'Start Time' },
      { value: 'end_time', text: 'End Time' },
      { value: 'band', text: 'Band' },
      { value: 'distance', text: 'Distance' },
      { value: 'max', text: 'Max HR' },
      { value: 'average', text: 'Avg HR' }
    ]
  },
  mounted() {
    if (!this.removable) {
      if (this.player.player_stats.CACGCSMXV) {
        this.tab = 'sprints'
      } else if (this.player.player_stats.CAPGCSMXAC) {
        this.tab = 'acc'
      } else if (this.player.player_stats.HRMAX) {
        this.tab = 'hr'
      } else {
        this.tab = 'sprints'
      }
      this.fetchPlayerRawData('vel_eff')
    } else {
      this.rawData.data = {
        sprints: Array.from({ length: 100 }, (_, i) => ({
          max: Math.round(Math.random() * 32),
          band: 3,
          start_time: i
        }))
      }
    }
  },
  methods: {
    async fetchPlayerRawData(data_type) {
      if ((data_type === 'vel_eff' && this.rawData.data.sprints.length > 0) ||
        (data_type === 'acc_eff' && this.rawData.data.high_acc.length > 0) ||
        (data_type === 'hr_eff' && this.rawData.data.hr.length > 0)) {
        return
      }

      this.rawData.loading = true

      try {
        const params = {
          player_id: this.player.id,
          data_type: data_type
        }

        let data = await EventApiService.getPlayerPerformanceData(this.eventIdForEfforts, params)

        if (data_type === 'vel_eff') {
          data = data.map(s => {
            s.start_time_min = Math.floor(s.start_time) + 'm ' + Math.floor((s.start_time - Math.floor(s.start_time)) * 60) + 's'
            s.end_time_min = Math.floor(s.end_time) + 'm ' + Math.floor((s.end_time - Math.floor(s.end_time)) * 60) + 's'
            s.duration_min = Math.floor(s.duration) + 'm ' + Math.floor((s.duration - Math.floor(s.duration)) * 60) + 's'

            s.max *= 3.6
            s.max = Number(s.max.toFixed(2))

            return s
          })
        }

        if (data_type === 'acc_eff') {
          data = data.map(s => {
            s.start_time_min = Math.floor(s.start_time) + 'm ' + Math.floor((s.start_time - Math.floor(s.start_time)) * 60) + 's'
            s.end_time_min = Math.floor(s.end_time) + 'm ' + Math.floor((s.end_time - Math.floor(s.end_time)) * 60) + 's'
            s.duration_min = Math.floor(s.duration) + 'm ' + Math.floor((s.duration - Math.floor(s.duration)) * 60) + 's'

            if (s.band <= 4) s.max = -Math.abs(s.max)

            return s
          })
        }

        if (data_type === 'vel_eff') {
          this.rawData.data.sprints = data
        } else if (data_type === 'acc_eff') {
          this.rawData.data.high_acc = data
        } else if (data_type === 'hr_eff') {
          this.rawData.data.hr = data
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There were some errors loading player stats',
          color: 'error'
        })

        throw e
      } finally {
        this.rawData.loading = false
      }
    },
    getColorForTooltip(val) {
      if (this.tab === 'sprints') {
        return this.sprintBandsColors[val - 1]
      } else if (this.tab === 'acc') {
        return val <= 4 ? '#00CC00' : '#00FF00'
      } else if (this.tab === 'hr') {
        return '#E91E63'
      }
    },
    getTooltipText(val) {
      if (this.tab === 'sprints') {
        return this.sprintBands[val - 1]
      } else if (this.tab === 'acc') {
        return this.accelerationBands[val - 1]
      } else if (this.tab === 'hr') {
        return this.hrBands[val - 1]
      }
    }
  }
}
</script>

<style scoped>
.indicator {
  transform: translateX(-50%);
  left: 50%;
}
</style>
